import { useMemo } from 'react'

import useHasContactedMap from '@modules/chat/hooks/useHasContactedMap'
import { FetchAttendeesFnResponse } from '@modules/search/api/profile'
import { Company, Delegate, ProfileType } from '@modules/search/types'
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query'

import searchApi from '../../search/api'

const DEFAULT_PAGE_LIMIT = 20

const useAttendees = ({ companyId, delegateId }: { companyId: Company['id']; delegateId?: Delegate['id'] }) => {
  const {
    data: { pages } = {},
    isInitialLoading,
    fetchNextPage,
  } = useInfiniteQuery<FetchAttendeesFnResponse>({
    queryKey: [searchApi.getAttendees.key, companyId, delegateId],
    queryFn: params =>
      searchApi.getAttendees.fn({ companyId, size: DEFAULT_PAGE_LIMIT, ...params, pageParam: params.pageParam as number }),
    initialPageParam: 0,
    placeholderData: keepPreviousData,
    getNextPageParam: (lastPage, allPages) => {
      if (allPages.length < Math.ceil(lastPage.total / DEFAULT_PAGE_LIMIT)) {
        return allPages.length
      }

      return undefined
    },
  })

  const attendees = useMemo(() => pages?.flatMap(({ result }) => result.map(i => ({ ...i.latest, id: i.id }))) || [], [pages])

  const contactedMap = useHasContactedMap({
    profileIds: [...attendees.map(i => ({ id: i.id, type: ProfileType.delegate })), { type: ProfileType.company, id: companyId }],
  })

  const total = useMemo(() => (pages && pages.length ? pages[0].total : 0), [pages])
  const eventData = useMemo(() => (pages && pages.length ? pages[0].event : null), [pages])

  return {
    fetchNextPage,
    attendees: attendees.map(i => ({ ...i, hasContacted: !!contactedMap?.[ProfileType.delegate]?.[i.id] })),
    total,
    eventData,
    isInitialLoading,
    hasContactedWithCompany: !!contactedMap?.[ProfileType.company]?.[companyId],
    hasContactedDelegate: !!delegateId && !!contactedMap?.[ProfileType.delegate]?.[delegateId],
  }
}

export default useAttendees
